import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Chip,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { apiService } from "../../config/api";
import { authenticatedApi } from "../../utils/api";
import { useUser } from "../../contexts/UserContext";
import StudyService from "../../services/studyService";

// Type definitions
export interface Template {
  id: string;
  template_name: string;
  is_custom: boolean;
  is_default: boolean;
  isSystemPreset?: boolean;
  alert_types?: string[];
  subject_line?: string;
  message_body?: string;
}

export interface AlertContactDialogProps {
  open: boolean;
  onClose: () => void;
  participantId?: string;
  eventId?: string;
  alertType?: string;
  alertReason?: string;
  startDate?: string;
  endDate?: string;
  anomalyDate?: string;
  contactTemplate?: Template;
  studyId?: string;
  onSent?: () => void;
}

export const AlertContactDialog: React.FC<AlertContactDialogProps> = ({
  open,
  onClose,
  participantId,
  eventId,
  alertType,
  alertReason,
  startDate,
  endDate,
  anomalyDate,
  contactTemplate,
  studyId,
  onSent,
}) => {
  const { userProfile } = useUser();
  const [messageBody, setMessageBody] = useState("");
  const [subjectLine, setSubjectLine] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null
  );
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [studyName, setStudyName] = useState<string>("");

  // Sample data for variable substitution
  const sampleData = useMemo(() => ({
    participant_id: participantId,
    participant_first_name: "Participant",
    study_name: studyName || studyId || "Study",
    device_type: "Fitbit",
    last_sync_date: new Date().toLocaleDateString(),
    alert_type: alertType || "Alert",
    alert_date: new Date().toLocaleDateString(),
    researcher_name: "Researcher",
    researcher_email: "researcher@example.com",
  }), [participantId, studyName, studyId, alertType]);

  const normalizeAlertType = useCallback((s?: string) =>
    (s || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\s*\(streak\)\s*$/, ""), []);

  const templateMatchesAlertType = useCallback((t: Template, type?: string) => {
    if (!type || !Array.isArray(t.alert_types)) return false;
    const want = normalizeAlertType(type);
    return t.alert_types.some((at) => normalizeAlertType(at) === want);
  }, [normalizeAlertType]);

  // Render template by replacing variables
  const renderTemplate = useCallback((text: string): string => {
    let rendered = text;
    const skipKeys = new Set(["researcher_name", "researcher_email"]);
    Object.entries(sampleData).forEach(([key, value]) => {
      if (skipKeys.has(key)) return; // leave these placeholders for backend to fill with real values
      const regex = new RegExp(`{{${key}}}`, "g");
      rendered = rendered.replace(regex, String(value));
    });
    return rendered;
  }, [sampleData]);

  // Fetch templates for the study
  const fetchTemplates = useCallback(async () => {
    if (!studyId) {
      return;
    }
    setLoadingTemplates(true);
    try {
      const token = await apiService.getAuthToken();
      if (!token) throw new Error("Authentication required");
      const response = await authenticatedApi.get<any>(
        `/api/templates/study/${studyId}`,
        token
      );
      if (response.success) {
        setTemplates(response.templates || []);
      }
    } catch (error) {
      // Silently handle error
    } finally {
      setLoadingTemplates(false);
    }
  }, [studyId]);

  // Find default template
  const getDefaultTemplate = useCallback((): Template | null => {
    if (templates.length === 0) return null;

    if (alertType) {
      const customDefault = templates.find(
        (t) =>
          t.is_custom && t.is_default && templateMatchesAlertType(t, alertType)
      );
      if (customDefault) return customDefault;

      const systemDefault = templates.find(
        (t) =>
          !t.is_custom && t.is_default && templateMatchesAlertType(t, alertType)
      );
      if (systemDefault) return systemDefault;
    }

    // Fallbacks: any default (custom first), then first matching, else first template
    const anyCustomDefault = templates.find((t) => t.is_custom && t.is_default);
    if (anyCustomDefault) return anyCustomDefault;
    const anySystemDefault = templates.find(
      (t) => !t.is_custom && t.is_default
    );
    if (anySystemDefault) return anySystemDefault;
    if (alertType) {
      const firstMatch = templates.find((t) =>
        templateMatchesAlertType(t, alertType)
      );
      if (firstMatch) return firstMatch;
    }
    return templates[0] || null;
  }, [templates, alertType, templateMatchesAlertType]);

  // Handle template selection
  const handleTemplateSelect = useCallback(
    (template: Template) => {
      setSelectedTemplate(template);
      const renderedSubject = renderTemplate(template.subject_line || "");
      const renderedBody = renderTemplate(template.message_body || "");
      setSubjectLine(renderedSubject);
      setMessageBody(renderedBody);
    },
    [renderTemplate]
  );

  // Initialize on dialog open
  useEffect(() => {
    if (open && studyId) {
      // Fetch templates if not already loaded
      if (templates.length === 0) {
        fetchTemplates();
      }
      setError("");
      setSuccess("");

      // Try to set default template only if templates are available
      if (templates.length > 0) {
        const defaultTemplate = getDefaultTemplate();
        if (defaultTemplate && !selectedTemplate) {
          handleTemplateSelect(defaultTemplate);
        } else if (!selectedTemplate && contactTemplate) {
          // Fallback to old behavior if no default template
          setSubjectLine("");
          setMessageBody(contactTemplate.message_body || "");
        }
      }
    }
  }, [
    open,
    studyId,
    templates.length,
    contactTemplate,
    selectedTemplate,
    getDefaultTemplate,
    handleTemplateSelect,
    fetchTemplates,
  ]);

  useEffect(() => {
    if (open && templates.length > 0 && !selectedTemplate && alertType) {
      const defaultTemplate = getDefaultTemplate();
      if (defaultTemplate) {
        handleTemplateSelect(defaultTemplate);
      }
    }
  }, [
    templates,
    templates.length,
    open,
    alertType,
    selectedTemplate,
    getDefaultTemplate,
    handleTemplateSelect,
  ]);

  // Fetch study name when dialog opens and studyId is available
  useEffect(() => {
    if (open && studyId) {
      const fetchStudyName = async () => {
        try {
          const token = await apiService.getAuthToken();
          if (token) {
            const response = await StudyService.getStudy(studyId, token);
            const study = response?.data || response?.study;
            if (study && study.name) {
              setStudyName(study.name);
            }
          }
        } catch (error) {
          // Fallback to studyId if fetch fails
          setStudyName(studyId);
        }
      };
      fetchStudyName();
    }
  }, [open, studyId]);


  const handleSend = async () => {
    setSending(true);
    setError("");
    setSuccess("");

    try {
      const token = await apiService.getAuthToken();
      const base = process.env.REACT_APP_API_URL || "";

      const resp = await fetch(`${base}/api/anomaly-detection/alerts/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          participantId,
          eventId,
          messageBody,
          subjectLine,
          alertType,
          alertReason,
          startDate,
          endDate,
          anomalyDate,
          studyId,
          templateId: selectedTemplate?.id || null,
          researcherName:
            userProfile?.firstName && userProfile?.lastName
              ? `${userProfile.firstName} ${userProfile.lastName}`
              : userProfile?.email || "Research Team",
          researcherEmail: userProfile?.email || "admin@studysyncs.com",
        }),
      });

      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${resp.status}`);
      }

      setSuccess("Email sent successfully!");

      // Close dialog after showing success message briefly
      setTimeout(() => {
        if (onSent) onSent();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err?.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Contact Participant</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Send a message to <Chip label={participantId} size="small" /> about
          this alert.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Template Selection */}
        {loadingTemplates ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : templates.length > 0 ? (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Template</InputLabel>
            <Select
              value={selectedTemplate?.id || ""}
              onChange={(e) => {
                const template = templates.find((t) => t.id === e.target.value);
                if (template) handleTemplateSelect(template);
              }}
              label="Select Template"
              disabled={sending || !!success}
            >
              {/* Show all templates; sort with best matches first */}
              {templates
                .slice()
                .sort((a, b) => {
                  const aMatch = templateMatchesAlertType(a, alertType) ? 1 : 0;
                  const bMatch = templateMatchesAlertType(b, alertType) ? 1 : 0;
                  if (aMatch !== bMatch) return bMatch - aMatch; // matches first
                  if (a.is_custom !== b.is_custom) return a.is_custom ? -1 : 1; // custom first
                  if ((a.is_default ? 1 : 0) !== (b.is_default ? 1 : 0))
                    return (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0); // defaults earlier
                  return a.template_name.localeCompare(b.template_name);
                })
                .map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {!template.is_custom && template.is_default
                      ? "[System Preset] "
                      : template.is_custom && template.is_default
                      ? "[Default] "
                      : ""}
                    {template.template_name}
                    {!template.is_custom && " (System)"}
                    {Array.isArray(template.alert_types) &&
                    template.alert_types.length > 0
                      ? ` — ${template.alert_types.join(", ")}`
                      : ""}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
            {loadingTemplates
              ? "Loading templates..."
              : templates.length === 0
              ? `No templates loaded. StudyId: ${studyId}, AlertType: ${alertType}`
              : !alertType
              ? "Alert type not available"
              : "No templates available"}
          </Typography>
        )}

        {/* Variable Replacement Note */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="caption">
            <strong>Note:</strong> Template variables (like researcher_name,
            researcher_email, alert_date, etc.) shown below will be replaced
            with actual data when the email is sent.
          </Typography>
        </Alert>

        {/* Subject Line */}
        <TextField
          label="Subject"
          fullWidth
          value={subjectLine}
          onChange={(e) => setSubjectLine(e.target.value)}
          sx={{ mb: 2 }}
          disabled={sending || !!success}
          multiline
          rows={2}
          placeholder="Email subject..."
        />

        {/* Message Body */}
        <TextField
          label="Message Body"
          multiline
          minRows={6}
          fullWidth
          value={messageBody}
          onChange={(e) => setMessageBody(e.target.value)}
          sx={{ mb: 2 }}
          disabled={sending || !!success}
          placeholder="Enter your message to the participant..."
        />

        <Box sx={{ mb: 1 }}>
          {alertType && (
            <Chip
              label={`Alert Type: ${alertType}`}
              size="small"
              sx={{ mr: 1 }}
            />
          )}
          {alertReason && (
            <Chip label={`Reason: ${alertReason}`} size="small" />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Anomaly Date:{" "}
          {anomalyDate ? new Date(anomalyDate).toLocaleDateString() : "N/A"}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          {success ? "Close" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !messageBody.trim() || !!success}
        >
          {sending ? "Sending..." : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertContactDialog;
